import cloneDeep from "lodash/cloneDeep";
import entries from "lodash/entries";
import flatMap from "lodash/flatMap";
import groupBy from "lodash/groupBy";
import map from "lodash/map";
import mapValues from "lodash/mapValues";
import merge from "lodash/merge";
import omit from "lodash/omit";
import pick from "lodash/pick";
import some from "lodash/some";
import values from "lodash/values";
import { Experience, Id, Importance, SkillsFramework, SkillsFrameworkRank } from "PFCore/types";
import { TFunction } from "react-i18next";

import {
  Conflict,
  FrameworksDecorator,
  SkillsFrameworkCustomField,
  SkillsFrameworkCustomValueDecoratedMerged,
  SkillsFrameworkDecorated,
  SkillsFrameworkSkillDecoratedMerged
} from "../skills_frameworks_side_panel.types";

export const decorateSkillsFrameworks = (selectedFrameworks: SkillsFramework[]): SkillsFrameworkDecorated[] =>
  selectedFrameworks.map((framework) => {
    const frameworkDecorator = pick(framework, ["id", "name", "rank"]);

    return merge(cloneDeep(framework), {
      skills: framework.skills.map(() => ({
        framework: frameworkDecorator
      })),
      skillsFrameworkCustomTypes: framework.skillsFrameworkCustomTypes.map((customType) => ({
        skillsFrameworkCustomValues: customType.skillsFrameworkCustomValues.map(() => ({
          framework: frameworkDecorator
        }))
      }))
    }) as SkillsFrameworkDecorated;
  });

export const getAllFrameworksCustomFields = (
  selectedFrameworks: SkillsFrameworkDecorated[]
): SkillsFrameworkCustomField<SkillsFrameworkCustomValueDecoratedMerged>[] =>
  map(
    values(
      groupBy(
        flatMap(selectedFrameworks, ({ skillsFrameworkCustomTypes }) => skillsFrameworkCustomTypes || []),
        "customTypeId"
      )
    ),
    (customTypeItems) => ({
      type: {
        id: customTypeItems[0].customTypeId,
        name: customTypeItems[0].name,
        displayAs: customTypeItems[0].displayAs
      },
      values: map(
        groupBy(flatMap(customTypeItems, "skillsFrameworkCustomValues"), "customValueId"),
        (items, customValueId) => ({
          customValueId: Number(customValueId),
          value: items[0].value,
          frameworks: map(items, "framework")
        })
      )
    })
  );

export const getSkillsGroupedById = (
  selectedFrameworks: SkillsFrameworkDecorated[]
): Record<Id, SkillsFrameworkSkillDecoratedMerged[]> => {
  const skillsGroupedById = groupBy(
    flatMap(selectedFrameworks, ({ skills }) => skills || []),
    "skillId"
  );

  return mapValues(skillsGroupedById, (skillVariants, skillId) =>
    map(
      groupBy(skillVariants, ({ importance, requiredExperience }) =>
        stringifySelectedValue({
          customValueId: Number(skillId),
          importance,
          requiredExperience
        })
      ),
      (items, stringifiedValue) => {
        const { importance, requiredExperience } = parseSelectedValueString(stringifiedValue);
        return {
          ...omit(items[0], "framework"),
          importance: importance as Importance,
          requiredExperience: requiredExperience as Experience,
          frameworks: map(items, "framework")
        };
      }
    )
  );
};

export const getResolvedAttributes = (
  selectedFrameworks: SkillsFrameworkDecorated[],
  conflicts: Conflict[],
  skillTypeId: Id
) => {
  const allCustomFields = getAllFrameworksCustomFields(selectedFrameworks);

  const customFieldsConflictsResolved = allCustomFields.map(({ type, values }) => {
    const conflict = findConflict(conflicts, type.id);

    if (!conflict) {
      return { type, values };
    }

    return {
      type,
      values: [values.find(({ customValueId }) => customValueId === conflict.selected.customValueId)!]
    };
  });

  const skillsById = getSkillsGroupedById(selectedFrameworks);

  const skillsConflictsResolved = entries(skillsById).map(([id, skills]) => {
    const skillConflict = findConflict(conflicts, skillTypeId, id);

    if (!skillConflict) {
      return skills[0];
    }

    return skills.find(
      ({ skillId, importance, requiredExperience }) =>
        skillId === skillConflict.selected.customValueId &&
        importance === skillConflict.selected.importance &&
        requiredExperience === skillConflict.selected.requiredExperience
    )!;
  });

  return {
    customFields: customFieldsConflictsResolved,
    skills: skillsConflictsResolved
  };
};

export const getConflictIdentifier = ({
  customFieldId,
  customValueId
}: Pick<Conflict, "customFieldId" | "customValueId">) => `${customFieldId}-${customValueId}`;

export const findConflict = (conflicts: Conflict[], customFieldId: Id, customValueId?: Id) =>
  conflicts.find(
    (conflict) => getConflictIdentifier(conflict) === getConflictIdentifier({ customFieldId, customValueId })
  );

export const isInPrimaryFramework = ({
  frameworks
}: SkillsFrameworkSkillDecoratedMerged | SkillsFrameworkCustomValueDecoratedMerged) =>
  some(frameworks, ({ rank }) => rank === SkillsFrameworkRank.Primary);

export const getDefaultConflictValue = (
  values: SkillsFrameworkSkillDecoratedMerged[] | SkillsFrameworkCustomValueDecoratedMerged[]
) => values.find(isInPrimaryFramework) || values[0];

export const stringifySelectedValue = ({
  customValueId,
  importance,
  requiredExperience
}: Conflict["selected"]): string => `${customValueId}-${importance}-${requiredExperience}`;

export const parseSelectedValueString = (selectedValueString: string): Conflict["selected"] => {
  const [customValueId, importance, requiredExperience] = selectedValueString.split("-").map((item) => {
    const parsed = Number(item);
    return isNaN(parsed) ? undefined : parsed;
  });

  return { customValueId: customValueId as number, importance, requiredExperience };
};

export const getFrameworksString = (frameworks: FrameworksDecorator["frameworks"], t: TFunction) =>
  map(frameworks, ({ name, rank }) => `${name} (${t(rank as unknown as TemplateStringsArray)})`).join(", ");
